// Canonical brand entity for Dream Coach Match.
//
// Single source of truth for the JSON-LD Organization that Google uses to
// build the Knowledge Graph entity. Every page that emits Organization,
// WebSite, Article publisher, or BreadcrumbList JSON-LD MUST reference these
// constants (and the shared @id) so signals consolidate onto the same entity
// rather than fragmenting into duplicates.

import { ICF_PARTNER_URL, SOCIAL_LINKS } from "~/lib/constants/socials";

export const BRAND_URL = "https://dreamcoachmatch.com";

/** Where users reach a person for account and billing help. */
export const SUPPORT_EMAIL = "support@dreamcoachmatch.com";

export const BRAND_ORGANIZATION_ID = `${BRAND_URL}/#organization`;
export const BRAND_WEBSITE_ID = `${BRAND_URL}/#website`;

// The About page is Builder-managed content served at the clean /about route
// (src/routes/_public/about.tsx); /page/about 301s there. The brand people
// anchor their JSON-LD @id to this URL so the Organization's `founder`
// reference and the home page's Person nodes consolidate onto one canonical
// node, and /about itself emits the matching Person JSON-LD (#757, #760).
export const BRAND_ABOUT_URL = `${BRAND_URL}/about`;
export const BRAND_FOUNDER_ID = `${BRAND_ABOUT_URL}#yuri-minski`;
export const BRAND_MARCEL_ID = `${BRAND_ABOUT_URL}#marcel-samyn`;

// Canonical brand strings (Website 2.0, issue #757). Ship byte-for-byte —
// em dash (—) not hyphen, curly apostrophe (’) not straight. One character
// off and schema validation fails. See docs/website-2.0/entity.md.
//
// - `description` is the master description (schema, meta, footer, body copy).
// - `organizationDescription` is the master description plus the ICF line,
//   used ONLY in Organization schema.
// - `slogan` is the brand lockup (hero + footer signature + schema slogan).
// - `categoryLine` replaces the retired "the world's coaching marketplace".
export const BRAND_DESCRIPTION =
  "Dream Coach Match is the global platform for coaching discovery, transformation, and professional growth — connecting verified coaches with the people they’re meant to serve.";

export const BRAND = {
  name: "Dream Coach Match",
  legalName: "Dream Coach Match Inc.",
  alternateName: "DreamCoachMatch",
  slogan: "The global home for coaching. Awaken what’s possible.",
  categoryLine: "the global platform for coaching",
  url: BRAND_URL,
  logo: `${BRAND_URL}/img/logo/dcm-horizontal-1.png`,
  email: "info@dreamcoachmatch.com",
  telephone: "+1-425-441-3867",
  foundingDate: "2024",
  description: BRAND_DESCRIPTION,
  // Organization schema only — master description + the ICF partner line.
  organizationDescription: `${BRAND_DESCRIPTION} Official Business Partner of the ICF (International Coaching Federation).`,
  address: {
    streetAddress: "c/o The Corporation Trust Company, 1209 Orange Street",
    addressLocality: "Wilmington",
    addressRegion: "DE",
    postalCode: "19801",
    addressCountry: "US",
  },
  // Topics we actually cover — a genuine topical-authority signal AI search
  // uses to pick sources. Lowercase per entity.md.
  knowsAbout: [
    "life coaching",
    "business coaching",
    "executive coaching",
    "health coaching",
    "career coaching",
    "leadership",
    "burnout",
    "relationships",
    "personal development",
  ],
  // The brand's verified profiles, for schema `sameAs` entity disambiguation:
  // our social profiles (SOCIAL_LINKS) plus the ICF partner listing. Only
  // profiles we control/can verify belong here; unverified URLs hurt
  // disambiguation rather than helping it.
  sameAs: [...Object.values(SOCIAL_LINKS), ICF_PARTNER_URL],
} as const;

/**
 * The postal address shown in the site footer, in the lead form and in emails
 * sent to people who have not signed up. Anti-spam law requires a postal
 * address where mail reaches us.
 */
export const BRAND_POSTAL_ADDRESS_LINES = [
  BRAND.address.streetAddress,
  "Wilmington, DE 19801, USA",
] as const;

/** `Dream Coach Match, c/o ..., Wilmington, DE 19801, USA` on one line. */
export const BRAND_POSTAL_ADDRESS_LINE = [
  BRAND.name,
  ...BRAND_POSTAL_ADDRESS_LINES,
].join(", ");

// The brand people surfaced in schema (home page Person nodes + Organization
// `founder`). All values confirmed by the team.
export const BRAND_PEOPLE = {
  yuri: {
    id: BRAND_FOUNDER_ID,
    name: "Yuri Minski",
    jobTitle: "Founder & CEO",
    description:
      "Founder and CEO of Dream Coach Match. A business and marketing leader (MBA, 20 years) and certified coach who built DCM after seeing, from inside the profession, why great coaches fail at the business.",
    url: BRAND_ABOUT_URL,
    image:
      "https://form-data.dreamcoachmatch.com/f55530ad-e07a-4799-a5f0-7f36b5429011-Yuri_Minski_1Q9A3830-cut.jpg",
    sameAs: ["https://www.linkedin.com/in/yuri-minski/"],
  },
  marcel: {
    id: BRAND_MARCEL_ID,
    name: "Marcel Samyn",
    jobTitle: "CTO",
    description:
      "Chief Technology Officer of Dream Coach Match, where he leads engineering and product. A software engineer and designer, he builds technology that amplifies human capability — the platform connecting coaches with the people they’re meant to serve.",
    url: BRAND_ABOUT_URL,
    sameAs: ["https://www.linkedin.com/in/marcelsamyn/"],
  },
} as const;
